/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card } from "@/shared/components/ui/card";
import { cn } from "@/infrastructure/lib/utils";
import { Settings } from '@mui/icons-material';
import { MoreVertical, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from 'react-i18next';
import { toast } from "@/shared/components/ui/use-toast";
import NodeConfigDialog from './NodeConfigDialog';
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Spinner } from '@/shared/components/ui/spinner';
import { useNodeType } from '../hooks/useNodeType';
import { WorkflowBaseService } from '../services/WorkflowBaseService';

// Mapa de cores por categoria
const NODE_COLORS: Record<string, string> = {
  API: '#2196F3',
  Databases: '#4CAF50',
  Files: '#FF9800',
  Embeddings: '#9C27B0',
  Triggers: '#F44336',
  other: '#607D8B'
};

interface CustomNodeData {
  label: string;
  title?: string;
  description?: string;
  type?: string;
  nodeId?: string;
  category?: string;
  config?: any;
  settings?: any;
  logo_url?: string;
  workflowId?: string;
  status?: 'success' | 'error' | 'running' | null;
  error?: {
    message: string;
    details?: string;
    code?: string;
    type?: string;
  };
  webhookStatus?: 'pending' | 'success' | 'error';
  loading?: boolean;
  name?: string;
}

function CustomNode({ data, id }: NodeProps<CustomNodeData>) {
  const { t } = useTranslation();
  // Definir a cor do nó com base na categoria
  const nodeColor = NODE_COLORS[data.category || 'other'] || NODE_COLORS.other;
  const { setNodes, getNode, getEdges, setEdges } = useReactFlow();
  const [configOpen, setConfigOpen] = useState(false);
  
  // Melhorar a detecção de nós do tipo trigger
  const isTriggerNode = data.type?.includes('trigger') || data.type?.includes('webhook');
  
  // Usar o status de qualquer origem (status ou webhookStatus)
  // Damos prioridade ao status padrão e usamos webhookStatus como fallback para compatibilidade
  const effectiveStatus = isTriggerNode 
    ? (data.status || data.webhookStatus) 
    : data.status;

  const { nodeTypeInfo, isLoading: isLoadingNodeType } = useNodeType(data.type || '');

  // Função para determinar a cor da borda baseada no status
  const getBorderColor = () => {
    if (effectiveStatus === 'success') {
      return 'border-green-500';
    } else if (effectiveStatus === 'error') {
      return 'border-red-500';
    } else if (effectiveStatus === 'running') {
      return 'border-blue-500';
    } else if (data.loading) {
      return 'border-blue-500';
    }
    // Usando a cor baseada na categoria para nós sem status
    return `border-[${nodeColor}]`;
  };

  // Atualizar o node quando as configurações forem salvas
  useEffect(() => {
    const handleConfigUpdate = (event: CustomEvent) => {
      const { id: nodeId, config } = event.detail;
      if (nodeId === id) {
        setNodes(nodes => nodes.map(node => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                config
              }
            };
          }
          return node;
        }));
      }
    };

    window.addEventListener('updateNodeConfig', handleConfigUpdate as EventListener);
    return () => {
      window.removeEventListener('updateNodeConfig', handleConfigUpdate as EventListener);
    };
  }, [id, setNodes]);

  const handleDelete = async () => {
    try {
      const node = getNode(id);
      if (!node) return;

      // Remover edges conectadas ao node
      const edges = getEdges();
      const edgesToRemove = edges.filter(
        edge => edge.source === id || edge.target === id
      );

      // Verificar se temos um ID de workflow válido
      if (!data.workflowId) {
        toast({
          title: t('workflow.node.delete_error'),
          description: t('workflow.node.missing_workflow_id'),
          variant: 'destructive',
        });
        return;
      }

      // Buscar o workflow atual
      const workflow = await WorkflowBaseService.getById(data.workflowId);

      // Interface para definir a estrutura do settings
      interface WorkflowSettings {
        nodes: Array<{ id: string, [key: string]: any }>;
        edges: Array<{ source: string, target: string, [key: string]: any }>;
        [key: string]: any;
      }
      
      // Garantir que temos um objeto de settings com a estrutura correta
      const workflowSettings = workflow?.settings as WorkflowSettings || { nodes: [], edges: [] };
      
      // Criar uma cópia segura com valores padrão
      const safeSettings: WorkflowSettings = {
        ...workflowSettings,
        nodes: Array.isArray(workflowSettings.nodes) ? workflowSettings.nodes : [],
        edges: Array.isArray(workflowSettings.edges) ? workflowSettings.edges : []
      };

      // Atualizar o settings removendo o node e suas edges
      const updatedSettings = {
        ...safeSettings,
        nodes: safeSettings.nodes.filter((n) => n.id !== id),
        edges: safeSettings.edges.filter(
          (e) => e.source !== id && e.target !== id
        )
      };

      // Atualizar o banco
      await WorkflowBaseService.update(data.workflowId, { settings: updatedSettings });

      // Usar edgesToRemove para remover as edges do estado local
      setNodes(nodes => nodes.filter(n => n.id !== id));
      setEdges(edges => edges.filter(
        e => !edgesToRemove.some(re => re.id === e.id)
      ));

      toast({
        title: t('common.success'),
        description: t('agents.workflow.nodeDeleted'),
      });

    } catch (error) {
      console.error('Error deleting node:', error);
      toast({
        title: t('common.error'),
        description: t('common.errors.deleting'),
        variant: "destructive",
      });
    }
  };

  // Combinando a detecção de clique com a abertura do dialog de configuração
  const handleClick = (event: React.MouseEvent) => {
    // Não abrir configurações se o clique foi no menu de opções
    const target = event.target as HTMLElement;
    if (target.closest('[data-node-menu]')) {
      return;
    }
    setConfigOpen(true);
  };

  return (
    <>
      <NodeConfigDialog
        node={getNode(id) || null}
        open={configOpen}
        onOpenChange={setConfigOpen}
      />
      <div className="relative focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 w-auto h-auto" onClick={handleClick}>
        <Card
          className={cn(
            "workflow-node-card", // Adicionando a classe para seleção CSS
            "flex flex-col min-w-[200px] max-w-[300px]",
            "transition-all duration-200",
            "hover:shadow-md",
            getBorderColor(),
            "border-2",
            {
              'rounded-none rounded-l-full rounded-r-lg': data.type?.includes('trigger') || data.type?.includes('webhook_trigger') || data.type?.includes('schedule_trigger'),
              'rounded-none rounded-l-lg rounded-r-full': data.type === 'end',
              'rounded-lg': !data.type?.includes('trigger') && !data.type?.includes('webhook_trigger') && !data.type?.includes('schedule_trigger') && data.type !== 'end'
            },
            "relative group"
          )}
        >
          {/* Conector de Entrada */}
          {(!data.type?.includes('trigger') && !data.type?.includes('webhook_trigger') && !data.type?.includes('schedule_trigger')) && (
            <Handle
              type="target"
              position={Position.Left}
              className={cn(
                "w-3 h-3 border-2",
                "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              )}
              style={{ 
                zIndex: 50,
                left: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: "#E5E7EB",
                borderColor: '#e7f2ff'
              }}
            />
          )}

          {/* Conectores de Saída */}
          {data.type === 'if_then' ? (
            <>
              {/* Conector para saída True */}
              <Handle
                id="true"
                type="source"
                position={Position.Right}
                className={cn(
                  "w-3 h-3 border-2",
                  "bg-background border-2 border-green-500",
                  "transition-colors duration-200",
                  "hover:border-primary"
                )}
                style={{ 
                  zIndex: 50,
                  right: '-8px',
                  top: '30%',
                  transform: 'translateY(-50%)'
                }}
              />
              {/* Label para o conector True */}
              <div 
                className="absolute text-xs text-green-600 font-semibold bg-white/90 px-1 rounded shadow-sm"
                style={{ 
                  left: 'calc(100% + 12px)',
                  top: '30%',
                  transform: 'translateY(-50%)',
                  zIndex: 40
                }}
              >
                True
              </div>

              {/* Conector para saída False */}
              <Handle
                id="false"
                type="source"
                position={Position.Right}
                className={cn(
                  "w-3 h-3 border-2",
                  "bg-background border-2 border-red-500",
                  "transition-colors duration-200",
                  "hover:border-primary"
                )}
                style={{ 
                  zIndex: 50,
                  right: '-8px',
                  top: '70%',
                  transform: 'translateY(-50%)'
                }}
              />
              {/* Label para o conector False */}
              <div 
                className="absolute text-xs text-red-600 font-semibold bg-white/90 px-1 rounded shadow-sm"
                style={{ 
                  left: 'calc(100% + 12px)',
                  top: '70%',
                  transform: 'translateY(-50%)',
                  zIndex: 40
                }}
              >
                False
              </div>
            </>
          ) : data.type === 'loop' ? (
            <>
              {/* Conector para saída Exit */}
              <Handle
                id="exit"
                type="source"
                position={Position.Right}
                className={cn(
                  "w-3 h-3 border-2",
                  "bg-background border-2 border-orange-500",
                  "transition-colors duration-200",
                  "hover:border-primary"
                )}
                style={{ 
                  zIndex: 50,
                  right: '-8px',
                  top: '30%',
                  transform: 'translateY(-50%)'
                }}
              />
              {/* Label para o conector Exit */}
              <div 
                className="absolute text-xs text-orange-600 font-semibold bg-white/90 px-1 rounded shadow-sm"
                style={{ 
                  left: 'calc(100% + 12px)',
                  top: '30%',
                  transform: 'translateY(-50%)',
                  zIndex: 40
                }}
              >
                Exit
              </div>

              {/* Conector para saída Loop */}
              <Handle
                id="loop"
                type="source"
                position={Position.Right}
                className={cn(
                  "w-3 h-3 border-2",
                  "bg-background border-2 border-blue-500",
                  "transition-colors duration-200",
                  "hover:border-primary"
                )}
                style={{ 
                  zIndex: 50,
                  right: '-8px',
                  top: '70%',
                  transform: 'translateY(-50%)'
                }}
              />
              {/* Label para o conector Loop */}
              <div 
                className="absolute text-xs text-blue-600 font-semibold bg-white/90 px-1 rounded shadow-sm"
                style={{ 
                  left: 'calc(100% + 12px)',
                  top: '70%',
                  transform: 'translateY(-50%)',
                  zIndex: 40
                }}
              >
                Loop
              </div>
            </>
          ) : (
            /* Conector de Saída padrão para outros tipos de nós */
            data.type !== 'end' && (
              <Handle
                type="source"
                position={Position.Right}
                className={cn(
                  "w-3 h-3 border-2",
                  "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                )}
                style={{ 
                  zIndex: 50,
                  right: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: "#E5E7EB",
                  borderColor: "#E5E7EB"
                }}
              />
            )
          )}

          {/* Loading Overlay */}
          {(data.loading || effectiveStatus === 'running') && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px] z-50 rounded-[inherit]">
              <div className="flex flex-col items-center justify-center">
                <Spinner className="w-8 h-8" />
              </div>
            </div>
          )}

          {/* Status Icon */}
          {(effectiveStatus === 'success' || effectiveStatus === 'error') && (
            <div className={cn(
              "absolute -top-2 -right-2 z-10",
              "rounded-full p-1",
              "bg-background shadow-sm ring-1 ring-border"
            )}>
              {effectiveStatus === 'success' ? (
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-500" />
              )}
            </div>
          )}

          {/* Cabeçalho do Node */}
          <div className={cn(
            "flex items-center justify-between p-3",
            "border-b border-border/50",
            {
              'rounded-l-full': data.type?.includes('trigger') || data.type?.includes('webhook_trigger') || data.type?.includes('schedule_trigger')
            },
            "bg-gradient-to-r from-background to-background/50"
          )}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className={cn(
                "w-8 h-8 rounded-md",
                "flex items-center justify-center",
                "bg-background/50 ring-1 ring-border",
                "transition-colors duration-200",
                "group-hover:ring-primary/30"
              )}>
                {isLoadingNodeType ? (
                  <Spinner className="w-4 h-4" />
                ) : nodeTypeInfo?.logo_url ? (
                  <img 
                    src={nodeTypeInfo.logo_url}
                    alt={nodeTypeInfo.name}
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.removeAttribute('style');
                    }}
                  />
                ) : data.logo_url ? (
                  <img 
                    src={data.logo_url}
                    alt={data.title || data.label || data.name}
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.removeAttribute('style');
                    }}
                  />
                ) : (
                  <Settings className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-sm truncate">
                    {data.title || data.label || data.name}
                  </span>
                </div>
                {data.type && (
                  <span className="text-xs text-muted-foreground">
                    {data.type}
                  </span>
                )}
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 p-0 ml-2 transition-opacity"
                  data-node-menu
                  onClick={(event) => event.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-node-menu>
                <DropdownMenuItem onClick={() => setConfigOpen(true)}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('common:configure')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={handleDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('common:delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mensagem de Erro */}
          {effectiveStatus === 'error' && data.error && (
            <div className="p-3 mt-auto border-t border-red-200 bg-red-50 dark:bg-red-900/10">
              <p className="text-xs font-medium text-red-600 dark:text-red-400">
                {data.error.message}
              </p>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

export default memo(CustomNode);
